import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, ClipboardPaste, Download, Loader2, RefreshCw, Search } from "lucide-react";

import { AdminGate } from "@/components/admin/AdminGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ActionSheet } from "@/components/admin/marketing/ActionSheet";
import { GrupoSection, subtotais } from "@/components/admin/marketing/GrupoSection";
import { GrupoTab } from "@/components/admin/marketing/GrupoTab";
import { InsightsSection } from "@/components/admin/marketing/InsightsSection";
import { PasteImporter } from "@/components/admin/marketing/PasteImporter";
import { PendenciasQueue } from "@/components/admin/marketing/PendenciasQueue";
import {
  getMarketingIntel,
  recalcMktActions,
  type MktAction,
} from "@/lib/marketing/intel.functions";
import { brl, CATEGORIAS, COMPLETUDE_LABEL, dec, int, periodo } from "@/lib/marketing/format";
import { GRUPOS, GRUPO_LABEL } from "@/lib/marketing/grupos";
import { camposFaltando, filaPendencias } from "@/lib/marketing/pendencias";

export const Route = createFileRoute("/admin/acoes-marketing")({
  head: () => ({
    meta: [
      { title: "Ações de Marketing — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/acoes-marketing">
      <AcoesPage />
    </AdminGate>
  ),
});

function AcoesPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["mkt-intel"],
    queryFn: () => getMarketingIntel(),
  });

  const [busca, setBusca] = useState("");
  const [cat, setCat] = useState("todas");
  const [comp, setComp] = useState("todas");
  const [aba, setAba] = useState<string>(GRUPOS[0]!.slug);
  const [open, setOpen] = useState<MktAction | null>(null);
  const [fila, setFila] = useState<MktAction[] | null>(null);
  const [colar, setColar] = useState(false);

  const actions = data?.actions ?? [];
  const produtos = data?.produtos ?? [];

  const filtered = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return actions.filter((a) => {
      if (cat !== "todas" && a.categoria !== cat) return false;
      if (comp !== "todas" && a.completude !== comp) return false;
      if (q && !a.nome.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [actions, busca, cat, comp]);

  const totais = useMemo(() => subtotais(filtered), [filtered]);

  const pendencias = useMemo(() => filaPendencias(filtered), [filtered]);
  const camposEmFalta = useMemo(
    () => pendencias.reduce((s, a) => s + camposFaltando(a).length, 0),
    [pendencias],
  );
  const notasAbertas = useMemo(() => actions.filter((a) => a.nota?.trim()).length, [actions]);

  const recalcAll = useMutation({
    mutationFn: () => recalcMktActions({ data: {} }),
    onSuccess: (r) => {
      toast.success(`${r.apuradas} ação(ões) apuradas · ${r.semBase} sem base para apurar`);
      qc.invalidateQueries({ queryKey: ["mkt-intel"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function exportCsv() {
    const cols: Array<[string, (a: MktAction) => string]> = [
      ["Grupo", (a) => a.grupo],
      ["Ação", (a) => a.nome],
      ["Categoria", (a) => a.categoria],
      ["Período", (a) => periodo(a)],
      ["Completude", (a) => COMPLETUDE_LABEL[a.completude] ?? a.completude],
      ["Sessões", (a) => String(a.sessoes ?? "")],
      ["Leads", (a) => String(a.leads ?? "")],
      ["Vendas", (a) => String(a.vendas ?? "")],
      ["Vendas (Eduzz)", (a) => String(a.vendas_eduzz ?? "")],
      ["Receita", (a) => String(a.receita ?? "")],
      ["Receita (Eduzz)", (a) => String(a.receita_eduzz ?? "")],
      ["Gasto total de mídia no período", (a) => String(a.investido_apurado ?? "")],
      ["Faturamento do site no período", (a) => String(a.faturamento_periodo ?? "")],
      ["Fonte da apuração", (a) => a.fonte_apuracao ?? ""],
      ["Investido", (a) => String(a.investido ?? "")],
      ["Ticket médio", (a) => String(a.ticket_medio ?? "")],
      ["CPV/CPL", (a) => String(a.cpv ?? "")],
      ["ROAS", (a) => String(a.roas ?? "")],
      ["Tx. conversão", (a) => String(a.taxa_conversao ?? "")],
      ["Fonte", (a) => a.fonte ?? ""],
      ["Nota", (a) => a.nota ?? ""],
    ];
    const linhas = [
      cols.map((c) => c[0]).join(";"),
      ...filtered.map((a) =>
        cols.map(([, fn]) => `"${fn(a).replace(/"/g, '""')}"`).join(";"),
      ),
    ];
    const url = URL.createObjectURL(
      new Blob(["\uFEFF" + linhas.join("\n")], { type: "text/csv;charset=utf-8" }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = `acoes-marketing-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="min-h-screen px-4 py-10">
      <div className="mx-auto max-w-[100rem]">
        <Link
          to="/admin/inteligencia-marketing"
          className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white/80 mb-2"
        >
          <ArrowLeft className="h-3 w-3" /> Inteligência de Marketing
        </Link>
        <h1 className="text-3xl font-bold text-white tracking-tight">Ações de Marketing</h1>
        <p className="text-sm text-white/60 mt-1 mb-6">
          Centro de inteligência das ações, agrupado por tipo de campanha. Cada número do relatório
          aparece ao lado do valor apurado nas vendas pagas da Eduzz (histórico do banco a partir de 01/06/2026).
        </p>

        <div className="grid gap-3 sm:grid-cols-5 mb-6">
          <Kpi label="Ações" valor={`${filtered.length}`} />
          <Kpi label="Vendas" valor={int(totais.vendas)} />
          <Kpi label="Receita" valor={brl(totais.receita)} />
          <Kpi
            label="Investido · ROAS"
            valor={`${brl(totais.investido)} · ${totais.roas === null ? "—" : dec(totais.roas, "x")}`}
          />
          <Kpi
            label="Cobertura de dados"
            valor={`${filtered.length === 0 ? 0 : Math.round((filtered.filter((a) => a.completude === "completo").length / filtered.length) * 100)}%`}
          />
        </div>


        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/30" />
            <Input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar ação…"
              className="pl-9 w-56 bg-white/5 border-white/10 text-white"
            />
          </div>
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger className="w-60 bg-white/5 border-white/10 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas as categorias</SelectItem>
              {CATEGORIAS.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={comp} onValueChange={setComp}>
            <SelectTrigger className="w-44 bg-white/5 border-white/10 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Toda completude</SelectItem>
              <SelectItem value="completo">Completo</SelectItem>
              <SelectItem value="parcial">Parcial</SelectItem>
              <SelectItem value="sem_dado">Sem dado</SelectItem>
            </SelectContent>
          </Select>

          <div className="ml-auto flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => recalcAll.mutate()}
              disabled={recalcAll.isPending}
              className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white text-xs"
            >
              {recalcAll.isPending ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              )}
              Recalcular pela Eduzz
            </Button>
            <Button
              variant="outline"
              onClick={() => setColar(true)}
              className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white text-xs"
            >
              <ClipboardPaste className="h-3.5 w-3.5 mr-1.5" /> Colar em bloco
            </Button>
            <Button
              variant="outline"
              onClick={exportCsv}
              className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white text-xs"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" /> CSV
            </Button>
          </div>
        </div>

        {isLoading && <div className="text-white/50 text-sm py-10 text-center">Carregando…</div>}

        {data && (
          <Tabs value={aba} onValueChange={setAba}>
            <TabsList className="flex h-auto flex-wrap justify-start gap-1 bg-white/5 p-1">
              {GRUPOS.map((g) => (
                <TabsTrigger
                  key={g.slug}
                  value={g.slug}
                  className="text-xs data-[state=active]:bg-white/15 data-[state=active]:text-white"
                >
                  {g.curto}
                  <span className="ml-1.5 text-white/40">
                    {filtered.filter((a) => a.grupo === g.slug).length}
                  </span>
                </TabsTrigger>
              ))}
              <TabsTrigger
                value="__all"
                className="text-xs data-[state=active]:bg-white/15 data-[state=active]:text-white"
              >
                Visão geral
              </TabsTrigger>
            </TabsList>

            {GRUPOS.map((g) => (
              <TabsContent key={g.slug} value={g.slug} className="mt-6">
                <GrupoTab
                  grupo={g}
                  rows={filtered.filter((a) => a.grupo === g.slug)}
                  onOpen={setOpen}
                  onFila={setFila}
                />
              </TabsContent>
            ))}

            <TabsContent value="__all" className="mt-6 space-y-4">
              {GRUPOS.map((g) => (
                <GrupoSection
                  key={g.slug}
                  grupo={{ label: g.label, descricao: g.descricao }}
                  rows={filtered.filter((a) => a.grupo === g.slug)}
                  onOpen={setOpen}
                  defaultOpen={false}
                />
              ))}
              {filtered.length === 0 && (
                <p className="text-sm text-white/40 py-8 text-center">
                  Nenhuma ação com os filtros atuais.
                </p>
              )}

              <div className="pt-8">
                <InsightsSection insights={data.insights} />
              </div>

              <section className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-bold text-white tracking-tight">
                      Pendências para fechar o compilado
                    </h2>
                    <p className="text-xs text-white/50 mt-1">
                      {pendencias.length} ações com campos em branco · {camposEmFalta} perguntas ·{" "}
                      {notasAbertas} nota(s)/pergunta(s) em aberto. Ticket, CPV, ROAS e conversão são
                      calculados pelo sistema. Sessões e leads antes de 22/06/2026 não contam (não
                      existe tracking no período).
                    </p>
                  </div>
                  <Button
                    onClick={() => setFila(pendencias)}
                    disabled={pendencias.length === 0}
                    className="bg-[#f5b022] hover:bg-[#e09f17] text-[#13123a] font-semibold"
                  >
                    Modo pergunta/resposta
                  </Button>
                </div>

                <div className="mt-5 grid gap-2 md:grid-cols-2">
                  {pendencias.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => setOpen(a)}
                      className="w-full text-left rounded-xl border border-white/10 bg-white/[0.03] p-4 hover:bg-white/[0.06] transition-colors"
                    >
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <span className="text-sm text-white/90">{a.nome}</span>
                        <span className="text-[10px] uppercase text-white/40">
                          {GRUPO_LABEL[a.grupo] ?? a.grupo}
                        </span>
                      </div>
                      <p className="mt-2 text-[11px] text-white/50">
                        Falta: {camposFaltando(a).map((c) => c.label).join(" · ")}
                      </p>
                      {a.nota && <p className="mt-1 text-[11px] text-amber-300/80">Nota: {a.nota}</p>}
                    </button>
                  ))}
                  {pendencias.length === 0 && (
                    <p className="text-sm text-white/40">
                      Nenhuma pendência: todas as ações filtradas estão completas.
                    </p>
                  )}
                </div>
              </section>
            </TabsContent>
          </Tabs>
        )}
      </div>

      {open && (
        <ActionSheet
          action={open}
          produtos={produtos}
          onClose={() => setOpen(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["mkt-intel"] });
            setOpen(null);
          }}
        />
      )}

      {fila && (
        <PendenciasQueue
          actions={fila}

          onClose={() => {
            setFila(null);
            qc.invalidateQueries({ queryKey: ["mkt-intel"] });
          }}
          onSaved={() => qc.invalidateQueries({ queryKey: ["mkt-intel"] })}
        />
      )}

      {colar && (
        <PasteImporter
          actions={actions}
          onClose={() => setColar(false)}
          onSaved={() => qc.invalidateQueries({ queryKey: ["mkt-intel"] })}
        />
      )}
    </div>
  );
}

function Kpi({ label, valor }: { label: string; valor: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
      <p className="text-[10px] uppercase tracking-wider text-white/40">{label}</p>
      <p className="mt-1 text-lg font-bold text-white">{valor}</p>
    </div>
  );
}
